/**
 * React hook for reading Replane config values reactively.
 *
 * Returns the hardcoded defaultValue on first render (before Replane connects),
 * then re-renders automatically with the live Replane value once the SDK is ready
 * AND on every subsequent change pushed over SSE (dashboard edits apply live).
 *
 * Usage:
 *   const maxLen = useReplaneConfig('frontend', 'validation.max_ai_short_text_length', 4000)
 */

import { useCallback, useEffect, useState } from "react"
import { getReplaneConfig, onReplaneReady, subscribeToReplaneConfig, asBool, asNumber, isReplaneSettled } from "./replaneClient"

export function useReplaneConfig(serviceKey, path, defaultValue) {
  const [value, setValue] = useState(() => getReplaneConfig(serviceKey, path, defaultValue))

  const refresh = useCallback(() => {
    setValue(getReplaneConfig(serviceKey, path, defaultValue))
  }, [serviceKey, path, defaultValue])

  useEffect(() => {
    // Re-read when Replane connects (initial SDK load)...
    const unsubscribeReady = onReplaneReady(refresh)
    // ...and on every live config-change pushed over SSE for this service, so a
    // dashboard edit re-renders the component immediately (polling = fallback).
    const unsubscribeChange = subscribeToReplaneConfig(serviceKey, refresh)
    refresh() // catch already-connected case (closes race between useState and useEffect)
    return () => {
      unsubscribeReady()
      unsubscribeChange()
    }
  }, [serviceKey, refresh])

  return value
}

/** Boolean-typed variant — coerces string 'true'/'false' from Replane dashboard. */
export function useReplaneConfigBool(serviceKey, path, defaultValue) {
  const raw = useReplaneConfig(serviceKey, path, defaultValue)
  return asBool(raw)
}

/** Number-typed variant — coerces string numbers from Replane dashboard. */
export function useReplaneConfigNumber(serviceKey, path, defaultValue) {
  const raw = useReplaneConfig(serviceKey, path, defaultValue)
  return asNumber(raw, defaultValue)
}

/**
 * True once Replane's first connect attempt has finished (connected, failed, or disabled), or
 * after `timeoutMs` as a backstop (a hung connect can take up to 30 s). Use it to hold a
 * source decision so the page doesn't render one data source and then swap to another.
 */
export function useReplaneSettled({ timeoutMs = 5000 } = {}) {
  const [settled, setSettled] = useState(() => isReplaneSettled())
  useEffect(() => {
    if (settled) return undefined
    const unsubscribe = onReplaneReady(() => setSettled(true))
    if (isReplaneSettled()) setSettled(true)
    const timer = setTimeout(() => setSettled(true), timeoutMs)
    return () => {
      unsubscribe()
      clearTimeout(timer)
    }
  }, [settled, timeoutMs])
  return settled
}
